// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { AccordionSectionPanel } from "@insite/mobius/AccordionSection";
import { AccordionSectionHeaderProps } from "@insite/mobius/AccordionSection/AccordionSectionHeader";
import AccordionSectionPresentationPropsDefault from "@insite/mobius/AccordionSection/presentationProps";
import CollapseClose from "@synd/XDL/Icons/AngleDownLarge";
import CollapseOpen from "@synd/XDL/Icons/AngleUpLarge";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { xBaseTheme } from "@synd/XDL/types";
import { color } from "@synd/XDL/Utilities";
import { css } from "styled-components";

export const accordionSectionSmall = {
    ...AccordionSectionPresentationPropsDefault,
    toggleIconProps: {
        color: "text.link",
        src: CollapseOpen,
        height: 13,
        width: 8,
        css: css`
            &:hover {
                color: ${color("common.ui9")};
            }
        `,
    },

    titleTypographyProps: {
        size: fontConfig.xSmall.size,
        weight: fontConfig.weight.bold,
        css: css`
            letter-spacing: 0;
            line-height: ${fontConfig.xSmall.lineHeight};
        `,
    },
    panelProps: {
        css: css`
            padding: 0 8px 8px;
            border: 1px solid ${color("common.border")};
            border-top: none;
            &:not(:last-of-type) {
                border-bottom: none;
            }
            font-size: ${fontConfig.xSmall.size}px;
            letter-spacing: 0;
            line-height: ${fontConfig.xSmall.lineHeight};
        `,
    },
    headerProps: {
        css: css`
            border: 1px solid ${color("common.border")};
            &:not(:last-of-type) {
                border-bottom: none;
            }
            ${({ expanded }: AccordionSectionHeaderProps) => expanded && "border-bottom: none;"}
            &&& button,
            &&& button:focus {
                margin-bottom: 0;
                padding: 8px;
                border: none;
                background: ${color("common.ui1")};
            }
        `,
    },
} as xBaseTheme["accordion"]["sectionDefaultProps"];

export default {
    defaultProps: {
        mergeCss: true,
    },
    sectionDefaultProps: {
        ...AccordionSectionPresentationPropsDefault,
        mergeCss: true,
        titleTypographyProps: {
            weight: fontConfig.weight.bold,
            size: 24,
            css: css`
                letter-spacing: 0;
                line-height: 32px;
            `,
        },
        toggleIconProps: {
            color: "text.main",
            src: CollapseClose,
            width: 22,
            height: 24,
            css: css`
                &:hover {
                    color: ${color("common.ui9")};
                }
            `,
        },
        headerProps: {
            css: css`
                background: ${color("common.ui1")};

                button {
                    padding: 0 0 12px 0;
                    border: none;
                    border-bottom: 1px solid ${color("common.border")};
                    background: ${color("common.ui1")};
                }

                &&& button,
                &&& ${/* sc-selector */ AccordionSectionPanel} + & button {
                    &:focus {
                        border: none;
                        border-bottom: 1px solid ${color("common.border")};
                        background: ${color("common.background")};
                        padding: 0 0 12px 0;
                        outline-width: 0;
                    }
                }

                ${({ expanded }: AccordionSectionHeaderProps) =>
                    !expanded &&
                    css`
                        &&& button,
                        &&& button:focus {
                            margin-bottom: 32px;
                        }

                        &&& button,
                        &&& button:focus,
                        ${AccordionSectionPanel} + & button:focus {
                            border-bottom-color: ${color("common.border")};
                        }
                    `}
            `,
        },
        panelProps: {
            css: css`
                border: none;
                padding: 16px 0 32px 0;
                font-size: ${fontConfig.regular.size};
                letter-spacing: 0;
                line-height: ${fontConfig.regular.lineHeight};
            `,
        },
    },
} as xBaseTheme["accordion"];
